import { useMemo } from 'react';
import type { EInvoiceRecord } from '@/api/compliance.api';

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'GENERATED': return 'bg-emerald-100 text-emerald-700';
    case 'PENDING': return 'bg-amber-100 text-amber-700';
    case 'CANCELLED': return 'bg-gray-100 text-gray-600';
    case 'FAILED': return 'bg-red-100 text-red-600';
    default: return 'bg-gray-100 text-gray-600';
  }
}

interface DashboardTabProps {
  eInvoices?: EInvoiceRecord[];
  onRetry: (ei: EInvoiceRecord) => void;
}

export default function DashboardTab({ eInvoices = [], onRetry }: DashboardTabProps) {
  const activeEInvoices = eInvoices || [];
  const today = new Date().toISOString().split('T')[0];

  const stats = useMemo(() => {
    const generated = activeEInvoices.filter((e) => e.status === 'GENERATED').length;
    const pending = activeEInvoices.filter((e) => e.status === 'PENDING').length;
    const cancelled = activeEInvoices.filter((e) => e.status === 'CANCELLED').length;
    const failed = activeEInvoices.filter((e) => e.status === 'FAILED').length;
    const total = activeEInvoices.length;
    const registeredToday = activeEInvoices.filter((e) => e.status === 'GENERATED' && e.createdAt?.split('T')[0] === today).length;
    const failedToday = activeEInvoices.filter((e) => e.status === 'FAILED' && e.createdAt?.split('T')[0] === today).length;
    const cancelledToday = activeEInvoices.filter((e) => e.status === 'CANCELLED' && (e.cancelledAt || '').split('T')[0] === today).length;

    return { generated, pending, cancelled, failed, total, registeredToday, failedToday, cancelledToday };
  }, [activeEInvoices, today]);

  const recentRegistrations = useMemo(() => {
    return [...activeEInvoices]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 10);
  }, [activeEInvoices]);

  const failedRegistrations = useMemo(() => {
    return activeEInvoices.filter((e) => e.status === 'FAILED');
  }, [activeEInvoices]);

  const barWidth = (count: number) => {
    if (stats.total === 0) return '0%';
    return `${Math.max(2, (count / stats.total) * 100)}%`;
  };

  return (
    <div className="space-y-6">
      {/* Today's Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-xs text-gray-500">Registered Today</span>
          </div>
          <div className="text-2xl font-semibold text-gray-900">{stats.registeredToday}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-amber-500" />
            <span className="text-xs text-gray-500">Pending</span>
          </div>
          <div className="text-2xl font-semibold text-gray-900">{stats.pending}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-xs text-gray-500">Failed Today</span>
          </div>
          <div className="text-2xl font-semibold text-gray-900">{stats.failedToday}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-gray-400" />
            <span className="text-xs text-gray-500">Cancelled Today</span>
          </div>
          <div className="text-2xl font-semibold text-gray-900">{stats.cancelledToday}</div>
        </div>
      </div>

      {/* Registration Status Chart */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Registration Status</h4>
        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-600">Generated</span>
              <span className="text-gray-900 font-semibold">{stats.generated}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-5 overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full flex items-center justify-end px-2 transition-all" style={{ width: barWidth(stats.generated) }}>
                {stats.generated > 0 && <span className="text-[10px] text-white font-semibold">{stats.generated}</span>}
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-600">Pending</span>
              <span className="text-gray-900 font-semibold">{stats.pending}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-5 overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full flex items-center justify-end px-2 transition-all" style={{ width: barWidth(stats.pending) }}>
                {stats.pending > 0 && <span className="text-[10px] text-white font-semibold">{stats.pending}</span>}
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-600">Cancelled</span>
              <span className="text-gray-900 font-semibold">{stats.cancelled}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-5 overflow-hidden">
              <div className="h-full bg-gray-400 rounded-full flex items-center justify-end px-2 transition-all" style={{ width: barWidth(stats.cancelled) }}>
                {stats.cancelled > 0 && <span className="text-[10px] text-white font-semibold">{stats.cancelled}</span>}
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-600">Failed</span>
              <span className="text-gray-900 font-semibold">{stats.failed}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-5 overflow-hidden">
              <div className="h-full bg-red-500 rounded-full flex items-center justify-end px-2 transition-all" style={{ width: barWidth(stats.failed) }}>
                {stats.failed > 0 && <span className="text-[10px] text-white font-semibold">{stats.failed}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Registrations */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Recent Registrations</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-3 py-2.5 text-left font-medium text-gray-500">Invoice No</th>
                <th className="px-3 py-2.5 text-left font-medium text-gray-500">Customer</th>
                <th className="px-3 py-2.5 text-left font-medium text-gray-500">IRN</th>
                <th className="px-3 py-2.5 text-left font-medium text-gray-500">Ack No</th>
                <th className="px-3 py-2.5 text-left font-medium text-gray-500">Registered At</th>
                <th className="px-3 py-2.5 text-center font-medium text-gray-500">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recentRegistrations.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-gray-400">No registrations yet</td>
                </tr>
              ) : (
                recentRegistrations.map((ei) => {
                  return (
                    <tr key={ei.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2.5 font-medium text-[#4f46e5]">{ei.invoiceNumber}</td>
                      <td className="px-3 py-2.5 text-gray-600">{(ei as any).customerName || '—'}</td>
                      <td className="px-3 py-2.5 text-gray-600 font-mono text-[10px]">{ei.irn ? `${ei.irn.slice(0, 12)}...` : '—'}</td>
                      <td className="px-3 py-2.5 text-gray-600 font-mono">{ei.ackNumber || '—'}</td>
                      <td className="px-3 py-2.5 text-gray-600">{formatDateTime(ei.createdAt)}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusBadge(ei.status)}`}>
                          {ei.status}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Failed Registrations */}
      {failedRegistrations.length > 0 && (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-2.5">
            <i className="ri-alert-line text-amber-600" />
            <span className="text-sm font-medium text-amber-800">
              {failedRegistrations.length} failed registration{failedRegistrations.length > 1 ? 's' : ''} need{failedRegistrations.length === 1 ? 's' : ''} attention
            </span>
          </div>

          <div className="bg-white border border-red-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Failed Registrations</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-3 py-2.5 text-left font-medium text-gray-500">Invoice No</th>
                    <th className="px-3 py-2.5 text-left font-medium text-gray-500">Error Code</th>
                    <th className="px-3 py-2.5 text-left font-medium text-gray-500">Error Message</th>
                    <th className="px-3 py-2.5 text-center font-medium text-gray-500">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {failedRegistrations.map((ei) => {
                    let errorCode = '—';
                    let errorMessage = '—';
                    try {
                      const resp = JSON.parse(ei.irpResponse);
                      if (resp.ErrorDetails?.[0]) {
                        errorCode = resp.ErrorDetails[0].ErrorCode || '—';
                        errorMessage = resp.ErrorDetails[0].ErrorMessage || '—';
                      }
                    } catch { /* ignore parse errors */ }

                    return (
                      <tr key={ei.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2.5 font-medium text-gray-900">{ei.invoiceNumber}</td>
                        <td className="px-3 py-2.5 text-red-600 font-mono">{errorCode}</td>
                        <td className="px-3 py-2.5 text-red-600">{errorMessage}</td>
                        <td className="px-3 py-2.5 text-center">
                          <button
                            onClick={() => onRetry(ei)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-[#4f46e5] bg-indigo-50 rounded hover:bg-indigo-100 cursor-pointer"
                          >
                            <i className="ri-refresh-line" /> Retry
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}